import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { StatusArtikla } from '../komponente/Cip'
import { kolicina, nivoKratko, oblikPoBroju } from '../lib/format'
import { NoviArtikal } from './NoviArtikal'
import type { Artikal, Grupa, Nalog, Pakovanje } from '../lib/tipovi'

const NAJVISE = 200

/** Katalog artikala — knjiga, ne kartice: 32px redovi, brojevi desno u uskom monou,
 *  puna linija mastila poslije svakog petog reda (DR-7A).
 *
 *  Izbor artikala je jedini upit koji ovaj ekran radi nad `artikli`; sve što vrati zavisi
 *  od politike `citanje_firme`, dakle od `moja_firma()` iz JWT-a. Fotografija artikla
 *  dolazi sa fazom 4 (C20) — do tada se kolona ne crta, umjesto praznog okvira.
 */
export function Katalog({ nalog }: { nalog: Nalog | null }) {
  const [artikli, postaviArtikle] = useState<Artikal[]>([])
  const [grupe, postaviGrupe] = useState<Grupa[]>([])
  const [pakovanja, postaviPakovanja] = useState<Pakovanje[]>([])
  const [upit, postaviUpit] = useState('')
  const [trazeno, postaviTrazeno] = useState('')
  const [ucitava, postaviUcitava] = useState(true)
  const [greska, postaviGresku] = useState<string | null>(null)
  const [izabran, postaviIzabran] = useState<string | null>(null)
  const [dodaje, postaviDodaje] = useState(false)
  // Brojač koji se poveća poslije snimanja, da se spisak ponovo pročita iz baze umjesto
  // da se novi artikal lijepi u zatečeno stanje na ekranu.
  const [osvjezi, postaviOsvjezi] = useState(0)

  // Kucanje ne šalje upit na svaki znak.
  useEffect(() => {
    const sat = setTimeout(() => postaviTrazeno(upit.trim()), 250)
    return () => clearTimeout(sat)
  }, [upit])

  useEffect(() => {
    let otkazano = false

    async function ucitaj() {
      postaviUcitava(true)
      postaviGresku(null)

      let upitArtikala = supabase
        .from('artikli')
        .select('id, sifra, naziv, grupa_id, osnovna_jedinica, brend, status')
        .order('sifra')
        .limit(NAJVISE)

      if (trazeno !== '') {
        // Pretraga bez dijakritike preko normalizuj() + pg_trgm (C34) ide uz Cmd+K;
        // ovdje je dovoljno poklapanje po šifri i nazivu.
        const bezbjedno = trazeno.replace(/[,()*]/g, ' ')
        upitArtikala = upitArtikala.or(`sifra.ilike.*${bezbjedno}*,naziv.ilike.*${bezbjedno}*`)
      }

      const [odgovorArtikli, odgovorGrupe] = await Promise.all([
        upitArtikala,
        supabase.from('grupe').select('id, naziv').order('naziv'),
      ])

      if (otkazano) return

      if (odgovorArtikli.error || odgovorGrupe.error) {
        postaviGresku((odgovorArtikli.error ?? odgovorGrupe.error)!.message)
        postaviUcitava(false)
        return
      }

      const redovi = (odgovorArtikli.data ?? []) as Artikal[]
      postaviArtikle(redovi)
      postaviGrupe((odgovorGrupe.data ?? []) as Grupa[])

      // Pakovanja samo za artikle koji su na ekranu, i samo aktivna (vazi_do is null).
      if (redovi.length > 0) {
        const odgovorPakovanja = await supabase
          .from('pakovanja')
          .select('artikal_id, nivo, sadrzi_osn, bar_kod, podrazumijevano_za_prodaju')
          .in(
            'artikal_id',
            redovi.map((r) => r.id),
          )
          .is('vazi_do', null)

        if (otkazano) return
        if (odgovorPakovanja.error) {
          postaviGresku(odgovorPakovanja.error.message)
        } else {
          postaviPakovanja((odgovorPakovanja.data ?? []) as Pakovanje[])
        }
      } else {
        postaviPakovanja([])
      }

      postaviUcitava(false)
    }

    void ucitaj()
    return () => {
      otkazano = true
    }
  }, [trazeno, osvjezi])

  const nazivGrupe = useMemo(() => new Map(grupe.map((g) => [g.id, g.naziv])), [grupe])

  // Po artiklu se prikazuje pakovanje prodaje; ako ga nema, najveće koje postoji.
  const pakovanjePoArtiklu = useMemo(() => {
    const mapa = new Map<string, Pakovanje>()
    for (const p of pakovanja) {
      const postojece = mapa.get(p.artikal_id)
      if (!postojece) {
        mapa.set(p.artikal_id, p)
        continue
      }
      if (p.podrazumijevano_za_prodaju && !postojece.podrazumijevano_za_prodaju) {
        mapa.set(p.artikal_id, p)
        continue
      }
      if (
        p.podrazumijevano_za_prodaju === postojece.podrazumijevano_za_prodaju &&
        Number(p.sadrzi_osn) > Number(postojece.sadrzi_osn)
      ) {
        mapa.set(p.artikal_id, p)
      }
    }
    return mapa
  }, [pakovanja])

  // Obrazac zauzima cio ekran umjesto da se otvara preko spiska: unos artikla ima tri
  // odjeljka i slike, pa u iskačućem prozoru ne bi stao bez skrolovanja unutar skrola.
  if (dodaje && nalog) {
    return (
      <NoviArtikal
        nalog={nalog}
        naOdustani={() => postaviDodaje(false)}
        naSacuvan={() => {
          postaviDodaje(false)
          postaviOsvjezi((n) => n + 1)
        }}
      />
    )
  }

  return (
    <>
      <div className="alati">
        <input
          className="polje"
          type="search"
          placeholder="Šifra ili naziv"
          aria-label="Pretraga kataloga"
          value={upit}
          onChange={(e) => postaviUpit(e.target.value)}
          data-testid="katalog-pretraga"
        />
        <span className="alati__broj" data-testid="katalog-broj-redova">
          <span className="broj">
            {artikli.length}
            {artikli.length === NAJVISE ? '+' : ''}
          </span>{' '}
          {oblikPoBroju(artikli.length, 'artikal', 'artikla', 'artikala')}
        </span>
        {/* Dugme vidi samo admin, jer `artikli` prima upis kroz politiku `upis_admin`.
            Sakriveno dugme je udobnost, a ne zaštita — upis svejedno odbija baza. */}
        {nalog?.uloga === 'admin' && (
          <button
            type="button"
            className="dugme dugme--glavno"
            onClick={() => postaviDodaje(true)}
            data-testid="katalog-novi"
          >
            Novi artikal
          </button>
        )}
      </div>

      {greska && (
        <div className="greska" role="alert" data-testid="katalog-greska">
          {greska}
        </div>
      )}

      {ucitava ? (
        <div className="ucitavanje" data-testid="katalog-ucitavanje">
          Učitavanje…
        </div>
      ) : artikli.length === 0 ? (
        <div className="prazno" data-testid="katalog-prazno">
          {trazeno === ''
            ? 'Katalog je prazan. Dodaj artikal ručno ili sačekaj uvoz iz starog sistema (C21).'
            : `Nema artikla za „${trazeno}".`}
        </div>
      ) : (
        <div className="tabela-okvir">
          <table className="tabela" data-testid="katalog-tabela">
            <thead>
              <tr>
                <th scope="col">Šifra</th>
                <th scope="col">Naziv</th>
                <th scope="col">Grupa</th>
                <th scope="col">Jedinica</th>
                <th scope="col">Pakovanje</th>
                <th scope="col">Bar kod</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {artikli.map((artikal) => {
                const pakovanje = pakovanjePoArtiklu.get(artikal.id)
                return (
                  <tr
                    key={artikal.id}
                    data-testid="katalog-red"
                    data-sifra={artikal.sifra}
                    aria-selected={izabran === artikal.id}
                    onClick={() => postaviIzabran(artikal.id)}
                  >
                    <td className="tabela__sifra">{artikal.sifra}</td>
                    <td className="tabela__naziv">
                      {artikal.naziv}
                      {artikal.brend && (
                        <span className="tabela__tiho"> · {artikal.brend}</span>
                      )}
                    </td>
                    <td>
                      {artikal.grupa_id ? (
                        (nazivGrupe.get(artikal.grupa_id) ?? '—')
                      ) : (
                        <span className="tabela__tiho">—</span>
                      )}
                    </td>
                    {/* Jedinica je riječ, ne broj: ostaje u Interu (DESIGN.md, Typography). */}
                    <td className="tabela__tiho">{artikal.osnovna_jedinica}</td>
                    <td className="tabela__broj desno">
                      {pakovanje
                        ? `1 ${nivoKratko(pakovanje.nivo)} × ${kolicina(pakovanje.sadrzi_osn)} ${artikal.osnovna_jedinica}`
                        : '—'}
                    </td>
                    <td className="tabela__broj">{pakovanje?.bar_kod ?? '—'}</td>
                    <td>
                      <StatusArtikla status={artikal.status} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
